import {
  EPassengerCategory,
  EPassengerTitle,
  EPaymentStatus,
  ETripType,
  IClientSegment,
  IClientTrip,
  ICustomer,
  IDiscountCard,
} from '../../common/types';
import { EOrderStatus } from '../../services/orders/types';
import { IResultsTrip } from '../../services/results/types';
import { ISearchParameters } from '../Search/types';

export interface ICartTripsIdentifier {
  [ETripType.OUTBOUND]: string;
  [ETripType.RETURN]: string | null;
}

export interface ICartPassenger {
  category?: string;
  age: number;
  discountCards?: {
    /** Matches the TTT discount card ID's, such as "TTT_VSC_YOUNG_PASS"*/
    id: string;
    /**
     * The discount card number.
     * This field is required when using the TTT Booking API's UpdateCart endpoint.
     */
    code?: string | null;
  }[];
  title?: EPassengerTitle;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  isCustomer?: boolean;
}

export interface ICartCustomer extends ICustomer {
  isSubscribedToNewsletter: boolean;
}

export interface ICreateCartPayload {
  outboundTripId: string;
  returnTripId?: string;
  passengers?: ICartPassenger[];
  customer?: ICartCustomer;
  partnerId?: string | null;
  /** Matches an id provided from a partner. It is used to keep track of the partner's orders and enables an order match between their system and our own */
  partnerExternalId?: string | null;
}

export type ICartTrip = IResultsTrip;

export interface ICartTrips {
  [ETripType.OUTBOUND]: ICartTrip | null;
  [ETripType.RETURN]: ICartTrip | null;
}

interface ICartOrder {
  id: string;
  paidPrice: number;
  priceCents: number;
  feeCents: number;
  customer: string;
  partnerId: string | null;
  partnerExternalId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  partner_id: string | null;
  orderStatus: { status: EOrderStatus }[];
  payment: { token: string; paymentStatus: { status: EPaymentStatus }[] };
}

/** The additional products sold with the tickets */
export enum EAncillaryType {
  /** Departure within 24 hours: cancel at no cost until the departure */
  LAST_MINUTE = 'LAST_MINUTE',
  /** Cancel For Any Reason: 90 % of the ticket price back (85 % on Ouigo), asked up to 48 hours before the departure */
  CFAR = 'CFAR',
  /** For the whole order: an SMS confirms the trip, and the support handles the requests of the customer first */
  ZEN_RELAX = 'ZEN_RELAX',
}

export interface IAncillaryOffer {
  type: EAncillaryType;
  priceCents: number;
  /** "Zen & Relax" also asks the carrier for the compensation of a late train */
  coversDelays?: boolean;
  /** "Zen & Relax" tells the platform, the delay and the cancellation of a train of the order by SMS: carriers with live data only */
  tracksTrains?: boolean;
  /** One item per covered ticket, or one for a product of the whole order */
  items: {
    ticketKey?: string;
    priceCents: number;
    /** Last moment the customer can use the product, ISO 8601. None when it lasts as long as the order. */
    deadlineUtc?: string;
    /** What the product refunds at least, when known */
    refundCents?: number;
    /** The same refund, as a share of the ticket price in percent */
    refundPercent?: number;
    /** The carrier refunds nothing for this ticket. None when unknown. */
    isNonRefundable?: boolean;
  }[];
}

export interface ICart {
  id: string;
  partnerId?: string;
  partnerExternalId?: string;
  /** The paid price from the customer perspective. Matches the sum of the trips price with all the fees */
  paidPrice: number;
  /** The actual price from Tictactrip's perspective. Matches the sum of the trips price without any fees. */
  rawPrice: number;
  totalPrice: number;
  order?: ICartOrder;
  trips: {
    outboundTrip: IClientTrip;
    returnTrip?: IClientTrip;
  };
  passengers?: {
    id: number;
    lastName: string;
    firstName: string;
    category: EPassengerCategory;
    discountCards: IDiscountCard[];
    title: EPassengerTitle;
    isCustomer: boolean;
    birthdate: string;
    age: number;
    discountCardId?: string;
  }[];
  customer?: {
    mail: string;
    lastName: string;
    firstName: string;
    title: EPassengerTitle;
    birthdate: string;
  };
  /** Additional products the customer can tick. Not included in paidPrice */
  ancillaryOffers?: IAncillaryOffer[];
  /** Additional products the customer ticked */
  ancillarySelection?: EAncillaryType[];
}

/**
 * @description Alternative versions of a segment.
 * The id is an arbitrary UUID and is just used to access an `ISegmentResponse`.
 */
export interface ISegmentAlternatives {
  [id: string]: IClientSegment;
}
export interface IUpdatedCart {
  cart: ICart;
  priceCentsDiff: number;
  isAvailable: boolean;
  segmentAlternatives: Record<IClientSegment['id'], ISegmentAlternatives>;
}

export interface IPatchCartPayload {
  passengers?: ICartPassenger[];
  customer?: ICartCustomer;
  segmentAlternatives?: { [id: string]: string };
  ancillaries?: EAncillaryType[];
}
export interface ICartContext {
  cartTrips: ICartTrips;
  addTripToCart: (trip: ICartTrip, direction: ETripType) => void;
  addTripsToCart: (trips: ICartTrips) => void;
  createCart(
    searchParameters: ISearchParameters,
    identifiers: ICartTripsIdentifier,
    partnerId?: string,
    partnerExternalId?: string,
  ): Promise<ICart | null>;
  getCart: (cartId: string, signal?: AbortSignal) => Promise<ICart>;
}
